/**
 * Per-tab ID bootstrap
 * --------------------
 * Provides a unique id for each browser tab, stored in sessionStorage.
 *
 * Requirements:
 *  - Each tab has a unique id in sessionStorage.
 *  - A normal reload of a tab KEEPS its id (sessionStorage survives reload).
 *  - Duplicating a tab must create a NEW id. (The browser copies sessionStorage
 *    into the duplicated tab, so the copied id must be detected and replaced.)
 *
 * How duplicate detection works:
 *  Tab duplication copies sessionStorage, so the new tab starts with the
 *  opener's id. We keep a registry of "live" tab ids in localStorage, where
 *  each tab writes a heartbeat timestamp. On load, if our sessionStorage id is
 *  already being heart-beaten by another live tab, this must be a duplicate ->
 *  mint a new id. If it is not live (fresh tab, or a reload where the previous
 *  instance's heartbeat is ours/stale), we keep it.
 *
 * The registry is stored under a reserved key and is always accessed through
 * the NATIVE localStorage methods so it is never affected by any per-tab
 * namespacing added later.
 */

import LS_SERVICE from "./localStorage";

// Capture native localStorage methods directly (not via the override module)
// to avoid a circular import. The registry key is also added to the override's
// RESERVED_KEYS so it is never namespaced, keeping both paths consistent.
const native = {
    getItem: window.localStorage.getItem.bind(window.localStorage),
    setItem: window.localStorage.setItem.bind(window.localStorage),
    removeItem: window.localStorage.removeItem.bind(window.localStorage),
    key: window.localStorage.key.bind(window.localStorage),
    get length() {
        return window.localStorage.length;
    },
};

/** True when this document is running inside an iframe (embedded). */
function isInIframe() {
    try {
        return window.self !== window.top;
    } catch (e) {
        // Cross-origin access to window.top throws -> we are definitely framed.
        return true;
    }
}

const TAB_ID_KEY = "__tabId"; // sessionStorage key for this tab's id
const REGISTRY_KEY = "__tabRegistry"; // localStorage key for the live-tab registry
const HEARTBEAT_INTERVAL_MS = 3000; // how often each tab refreshes its heartbeat
const STALE_TIMEOUT_MS = 10000; // a tab id is considered dead after this long without a heartbeat

let currentTabId = null;
let heartbeatTimer = null;
let wasDuplicated = false; // true when THIS load was detected as a tab duplication

function generateId() {
    // Prefer crypto.randomUUID when available; fall back to a random string.
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return (
        "tab-" +
        Date.now().toString(36) +
        "-" +
        Math.random().toString(36).slice(2, 10)
    );
}

/**
 * Tab duplication policy, read from localStorage
 * `EMR_CONFIGURATION_DATA.duplicate_tab_config_type`:
 *   - 1 : DO NOT allow duplicate tabs (default when no value is present).
 *   - 2 : ALLOW duplicate tabs.
 */
function getDuplicateTabConfigType() {
    try {
        const config = LS_SERVICE.get("EMR_CONFIGURATION_DATA");
        const value = config && config?.duplicate_tab_config_type;
        const parsed = parseInt(value, 10);
        return parsed === 2 ? 2 : 1; // default to 1 (do not allow)
    } catch (e) {
        return 1;
    }
}

function readRegistry() {
    try {
        const raw = native.getItem(REGISTRY_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
        return {};
    }
}

function writeRegistry(registry) {
    try {
        native.setItem(REGISTRY_KEY, JSON.stringify(registry));
    } catch (e) {
        // ignore write failures (e.g. storage full / disabled)
    }
}

/**
 * Remove registry entries whose last heartbeat is older than the stale timeout,
 * and purge the namespaced localStorage data of those dead tab ids.
 *
 * This is how CLOSED (and crashed) top-level tabs get cleaned up: a tab that
 * is closed stops heart-beating and never comes back, so it goes stale and its
 * `${id}:*` keys are purged here. A RELOAD resumes its heartbeat within the
 * stale window, so it is never considered dead and its data is preserved.
 */
function pruneStale(registry, now) {
    let changed = false;
    Object.keys(registry).forEach((id) => {
        if (now - registry[id] > STALE_TIMEOUT_MS) {
            delete registry[id];
            changed = true;
            // Clean up the dead tab's per-tab data. Never purge the tab that is
            // currently running (defensive — a live tab shouldn't be stale).
            if (id !== currentTabId) {
                purgeNamespace(id);
            }
        }
    });
    return changed;
}

/** Is this id currently kept alive by a (different) live tab? */
function isLive(registry, id, now) {
    return (
        Object.prototype.hasOwnProperty.call(registry, id) &&
        now - registry[id] <= STALE_TIMEOUT_MS
    );
}

function writeHeartbeat() {
    if (!currentTabId) return;
    const now = Date.now();
    const registry = readRegistry();
    pruneStale(registry, now);
    registry[currentTabId] = now;
    writeRegistry(registry);
}

function removeFromRegistry() {
    if (!currentTabId) return;
    const registry = readRegistry();
    if (Object.prototype.hasOwnProperty.call(registry, currentTabId)) {
        delete registry[currentTabId];
        writeRegistry(registry);
    }
}

/** Collect all physical `${tabId}:*` keys currently in localStorage. */
function namespaceKeys(tabId) {
    const prefix = `${tabId}:`;
    const keys = [];
    for (let i = 0; i < native.length; i++) {
        const physicalKey = native.key(i);
        if (physicalKey && physicalKey.indexOf(prefix) === 0) {
            keys.push(physicalKey);
        }
    }
    return keys;
}

/** Remove every per-tab (namespaced) localStorage key for the given tab id. */
function purgeNamespace(tabId) {
    if (!tabId) return;
    namespaceKeys(tabId).forEach((k) => native.removeItem(k));
}

const BACKUP_KEY = "__tabBackup"; // sessionStorage key holding this tab's data across a reload

/**
 * On unload: snapshot this tab's namespaced data into sessionStorage, then
 * purge it from localStorage immediately. sessionStorage survives a RELOAD but
 * is destroyed when the tab is CLOSED — so:
 *   - Close  -> localStorage purged now, backup dies with the tab -> clean. ✅
 *   - Reload -> localStorage purged now, backup survives -> restored on load. ✅
 */
let unloadHandled = false;
function backupAndPurgeOnUnload() {
    const id = currentTabId;
    if (!id) return;
    // pagehide and beforeunload can both fire; only do the work once.
    if (unloadHandled) return;
    unloadHandled = true;

    try {
        const keys = namespaceKeys(id);
        const snapshot = {};
        keys.forEach((k) => {
            snapshot[k] = native.getItem(k);
        });
        window.sessionStorage.setItem(
            BACKUP_KEY,
            JSON.stringify({ id, data: snapshot })
        );
    } catch (e) {
        // If we can't back up, don't purge (avoid data loss on reload).
        removeFromRegistry();
        return;
    }

    purgeNamespace(id);
    removeFromRegistry();
}

/**
 * On load (reload only): if a backup exists for THIS tab id, restore the
 * namespaced data into localStorage and clear the backup. On a real close the
 * backup never exists (sessionStorage died), so nothing is restored.
 */
function restoreBackupIfReload(tabId) {
    let raw = null;
    try {
        raw = window.sessionStorage.getItem(BACKUP_KEY);
    } catch (e) {
        raw = null;
    }
    if (!raw) return;

    try {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.id === tabId && parsed.data) {
            Object.keys(parsed.data).forEach((physicalKey) => {
                const val = parsed.data[physicalKey];
                if (val !== null && val !== undefined) {
                    native.setItem(physicalKey, val);
                }
            });
        }
    } catch (e) {
        // ignore malformed backup
    } finally {
        try {
            window.sessionStorage.removeItem(BACKUP_KEY);
        } catch (e) {
            // ignore
        }
    }
}

/**
 * Full cleanup for this tab/iframe: remove its namespaced data, its registry
 * entry, and its sessionStorage id. Used on iframe unload so an embedded view
 * leaves no residue behind.
 */
function cleanupThisTab() {
    const id = currentTabId;
    removeFromRegistry();
    purgeNamespace(id);
    try {
        window.sessionStorage.removeItem(TAB_ID_KEY);
    } catch (e) {
        // ignore
    }
}

/**
 * Was this page load a reload / hard refresh (as opposed to a fresh navigation
 * or a tab duplication)?
 *
 * On a reload the browser preserves sessionStorage, so the existing tab id must
 * be kept. We use the Navigation Timing API, which reliably reports "reload"
 * for both normal (F5) and hard (Ctrl+Shift+R) refreshes.
 */
function isReloadNavigation() {
    try {
        const navEntries =
            performance.getEntriesByType &&
            performance.getEntriesByType("navigation");
        if (navEntries && navEntries.length > 0) {
            return navEntries[0].type === "reload";
        }
        // Legacy fallback (deprecated but widely supported).
        if (performance.navigation) {
            return performance.navigation.type === 1; // TYPE_RELOAD
        }
    } catch (e) {
        // ignore and treat as non-reload
    }
    return false;
}

/**
 * Resolve this tab's id, detecting duplication and minting a new id when needed.
 * Idempotent: safe to call multiple times; only runs the resolution once.
 */
export function initTabId() {
    if (currentTabId) return currentTabId;

    const inIframe = isInIframe();

    let tabId;

    if (inIframe) {
        // Embedded (iframe): the application is being opened inside another
        // application. Only one iframe instance exists at a time, so we always
        // start clean: remove any inherited tab-id-specific details (namespaced
        // data + registry entry for the id copied in via sessionStorage), then
        // mint a brand-new id and open the page. We intentionally skip
        // reload/duplicate detection, blocking, and the landing-page redirect
        // here — the embedded view must always open on its own URL.

        // Purge the inherited id's details, if any, so nothing lingers.
        let inheritedId = null;
        try {
            inheritedId = window.sessionStorage.getItem(TAB_ID_KEY);
        } catch (e) {
            inheritedId = null;
        }
        if (inheritedId) {
            const inheritedRegistry = readRegistry();
            if (
                Object.prototype.hasOwnProperty.call(
                    inheritedRegistry,
                    inheritedId
                )
            ) {
                delete inheritedRegistry[inheritedId];
                writeRegistry(inheritedRegistry);
            }
            purgeNamespace(inheritedId);
        }

        tabId = generateId();
        currentTabId = tabId;

        try {
            window.sessionStorage.setItem(TAB_ID_KEY, tabId);
        } catch (e) {
            // sessionStorage unavailable; keep tabId in memory for this session.
        }

        writeHeartbeat();
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        heartbeatTimer = setInterval(writeHeartbeat, HEARTBEAT_INTERVAL_MS);

        // On iframe unload, fully clean up this tab's id + namespaced data so
        // nothing is left behind when the iframe unmounts/closes.
        window.addEventListener("pagehide", cleanupThisTab);
        window.addEventListener("beforeunload", cleanupThisTab);

        console.log("[tabId] iframe tab id:", currentTabId);
        return currentTabId;
    }

    const now = Date.now();
    const registry = readRegistry();
    pruneStale(registry, now);

    tabId = null;
    try {
        tabId = window.sessionStorage.getItem(TAB_ID_KEY);
    } catch (e) {
        tabId = null;
    }

    const reload = isReloadNavigation();

    if (!tabId) {
        // Fresh tab (no id in sessionStorage) -> mint a new one.
        tabId = generateId();
    } else if (reload) {
        // Reload / hard refresh -> ALWAYS keep the existing id. Never treat a
        // refresh as a duplicate, regardless of registry state.
    } else if (isLive(registry, tabId, now)) {
        // Not a reload, and the id we inherited via sessionStorage is already in
        // active use by another live tab -> this is a duplicated tab.
        wasDuplicated = true;
        if (getDuplicateTabConfigType() === 2) {
            // Policy 2: ALLOW duplicates. Keep running with the current session
            // tab id (the inherited id) so the duplicated tab behaves as its
            // own live tab.
            tabId = generateId();
        }
        // When duplicates are NOT allowed we intentionally keep the inherited
        // id untouched; the duplicate is blocked with a popup below and the
        // app is never allowed to mount.
    }
    // else: id exists, not a reload, not live elsewhere -> treat as same tab,
    // keep it.

    currentTabId = tabId;

    try {
        window.sessionStorage.setItem(TAB_ID_KEY, tabId);
    } catch (e) {
        // sessionStorage unavailable; we still keep tabId in memory for this session.
    }

    // If this load is a reload, restore the namespaced data that was backed up
    // (and purged from localStorage) by the previous unload. On a real close
    // the backup is gone, so nothing is restored and localStorage stays clean.
    restoreBackupIfReload(currentTabId);

    // Register our heartbeat immediately and keep it fresh.
    writeHeartbeat();

    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = setInterval(writeHeartbeat, HEARTBEAT_INTERVAL_MS);

    // On unload: snapshot this tab's data to sessionStorage and purge it from
    // localStorage NOW. Close -> stays purged; reload -> restored on next load.
    window.addEventListener("pagehide", backupAndPurgeOnUnload);
    window.addEventListener("beforeunload", backupAndPurgeOnUnload);

    // Back/forward cache (bfcache) restore: the page is shown again WITHOUT
    // re-running this module, so restore the backup we made on pagehide and
    // re-arm the unload handler.
    window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
            restoreBackupIfReload(currentTabId);
            unloadHandled = false;
            writeHeartbeat();
        }
    });

    console.log("[tabId] resolved tab id:", currentTabId, wasDuplicated ? "(duplicated)" : "");

    // Handle a detected duplication according to policy. Done here (very early,
    // before app modules load) so the duplicated route never mounts.
    if (wasDuplicated) {
        if (getDuplicateTabConfigType() === 2) {
            // Policy 2: duplicates allowed. Send the new tab to the post-login
            // landing page instead of restoring the duplicated screen.
            redirectDuplicatedTabToLanding();
        } else {
            // Policy 1: duplicates not allowed. Block this tab with a full-page
            // message telling the user the application is already open.
            blockDuplicatedTab();
        }
    }

    return currentTabId;
}

/**
 * Redirect a duplicated tab to the user's post-login landing page.
 * currentUser is a shared localStorage key, so a duplicated tab is still logged
 * in and can read its landing page. No-op if not logged in or already there.
 */
function redirectDuplicatedTabToLanding() {
    try {
        const raw = native.getItem("currentUser");
        const currentUser = raw ? JSON.parse(raw) : null;
        const landing = currentUser && currentUser.defaultLandingModulePage;
        if (!landing) return;

        const target = `/${String(landing).replace(/^\//, "")}`;
        if (window.location.pathname !== target) {
            window.location.replace(target);
        }
    } catch (e) {
        // On any failure, let the app load normally.
        console.log("[tabId] duplicate landing redirect skipped:", e);
    }
}

/**
 * Block a duplicated tab when the ALLOW_DUPLICATE_TAB policy is false.
 *
 * The app must never mount in this tab, so we:
 *  - stop this tab's heartbeat and remove the unload backup/purge handlers so
 *    the duplicate does not touch the live tab's registry entry or namespaced
 *    data, and
 *  - render a plain-DOM blocking overlay (no React dependency, since this runs
 *    during the very-early bootstrap) asking the user to close the tab.
 */
function blockDuplicatedTab() {
    try {
        // Stop heart-beating; this duplicate must not appear "live".
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
        }
        // Remove our registry entry so the duplicate leaves no residue.
        removeFromRegistry();
        // Do not run the backup/purge-on-unload logic for this blocked tab, so
        // closing it cannot disturb the real tab's data.
        window.removeEventListener("pagehide", backupAndPurgeOnUnload);
        window.removeEventListener("beforeunload", backupAndPurgeOnUnload);
    } catch (e) {
        // ignore cleanup failures
    }

    renderDuplicateBlockPage();
}

/**
 * Replace the whole page with a full-screen message informing the user that
 * the application is already open in another tab. No popup, no buttons — just
 * the message centered on the page. Pure DOM so it works before the React app
 * mounts.
 */
function renderDuplicateBlockPage() {
    const build = () => {
        const page = document.createElement("div");
        page.id = "__duplicateTabBlocker";
        page.style.cssText = [
            "position:fixed",
            "top:0",
            "left:0",
            "width:100vw",
            "height:100vh",
            "z-index:2147483647",
            "background:#ffffff",
            "display:flex",
            "align-items:center",
            "justify-content:center",
            "text-align:center",
            "padding:24px",
            "box-sizing:border-box",
            "font-family:Arial, Helvetica, sans-serif",
        ].join(";");

        const msg = document.createElement("p");
        msg.textContent =
            "The application is open already on another tab, please close this tab and continue on the previous tab.";
        msg.style.cssText = [
            "margin:0",
            "max-width:600px",
            "font-size:18px",
            "line-height:1.6",
            "color:#1a1a1a",
        ].join(";");

        page.appendChild(msg);

        // Blank the page and show only our message.
        const root = document.body || document.documentElement;
        root.innerHTML = "";
        root.appendChild(page);
    };

    if (document.body) {
        build();
    } else {
        document.addEventListener("DOMContentLoaded", build);
    }
}

/** Get the current tab id (initializing on first use). */
export function getTabId() {
    return currentTabId || initTabId();
}

/**
 * True if this page load was detected as a tab duplication (a new id was minted
 * because the inherited id was already live in another tab). Used to redirect
 * duplicated tabs to the post-login landing page.
 */
export function isDuplicatedTab() {
    // Ensure resolution has run.
    if (!currentTabId) initTabId();
    return wasDuplicated;
}

// Self-initialize on import so a side-effect import guarantees the tab id is
// resolved before app code runs. initTabId() is idempotent.
initTabId();

export default getTabId;
